import React from 'react'

import { useLightMode } from '@/hooks/useLightMode'

import { Box } from '@chakra-ui/react'

import {
  Alert,
  CloseIcon,
  Box as DarkBox,
  DarkProvider,
  IconButton,
} from '@hivewatch/dark'

/** Matches the Alert's 22px glyph row so the control sits on the same baseline. */
const DISMISS_BUTTON_SIZE = 22

/** Mirrors the Alert's own horizontal padding. */
const ALERT_INLINE_PADDING = '13px'

interface StaleBannerViewIProps {
  dismissAriaLabel: string
  message: string
  onDismiss: () => void
  onPrimaryButtonClick: () => void
  primaryButtonText: string
  testId: string
}

/**
 * Presentational shell for the stale work banner: a dark Alert whose trailing
 * action button is the single click affordance (ENG-32131 replaced the
 * whole-banner click from ENG-32130), plus an absolutely positioned dismiss X.
 * The action button and the dismiss control are sibling interactive elements,
 * which resolves the nested-interactive a11y limitation the clickable root
 * had. Mirrors StaticBanner's composition (kept separate because StaticBanner
 * has no action button and product-messages must stay untouched; see the
 * consolidation follow-up noted in the PR).
 */
export const StaleBannerView = ({
  dismissAriaLabel,
  message,
  onDismiss,
  onPrimaryButtonClick,
  primaryButtonText,
  testId,
}: StaleBannerViewIProps) => {
  const colorScheme = useLightMode()

  return (
    <Box data-testid={testId} width='100%' zIndex='banner'>
      <DarkProvider>
        <DarkBox position='relative' width='100%'>
          <Alert
            colorScheme={colorScheme}
            isCentered={true}
            primaryButton={{
              onClick: onPrimaryButtonClick,
              text: primaryButtonText,
            }}
            title={message}
            variant='info'
          />
          <IconButton
            aria-label={dismissAriaLabel}
            colorScheme={colorScheme}
            data-testid={`${testId}-dismiss`}
            dimension={DISMISS_BUTTON_SIZE}
            icon={<CloseIcon />}
            onClick={onDismiss}
            position='absolute'
            right={ALERT_INLINE_PADDING}
            top='50%'
            transform='translateY(-50%)'
          />
        </DarkBox>
      </DarkProvider>
    </Box>
  )
}
